/**
 * Identidade lógica ESTÁVEL das entradas de uma rota.
 *
 * Regras vinculativas:
 * - o ID é criado UMA única vez, no momento em que a paragem/refeição/bloco é
 *   aceite para aquela geração da rota;
 *
 * - o ID nunca deriva do dia, da hora, do índice do array, da posição visual
 *   nem da revisão: uma visita que muda de dia continua a ser a mesma visita;
 * - locais repetidos recebem um `occurrenceId` determinístico criado também
 *   uma única vez;
 * - módulo puro: sem IO, sem relógio, sem `Math.random`.
 */

export type LogicalEntryKind = "stop" | "meal" | "logistics" | "free_time";

function slug(value: string): string {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

export type LogicalIdRegistry = {
  /** Começa uma nova passagem de construção (recálculo da mesma rota). */
  beginPass(): void;
  /** ID lógico estável para a n-ésima ocorrência desta chave. */
  idFor(kind: LogicalEntryKind, key: string): string;
  /** Esquece tudo — usado apenas quando começa uma NOVA geração. */
  reset(): void;
  /** Número de identidades já atribuídas (diagnóstico/testes). */
  size(): number;
};

/**
 * Registo de identidades lógicas de UMA geração (`routeInstanceId`).
 * A mesma chave na mesma ocorrência devolve sempre o mesmo ID, em qualquer
 * recálculo, mudança de modo de transporte ou reordenação de dias.
 */
export function createLogicalIdRegistry(): LogicalIdRegistry {
  const assigned = new Map<string, string>();
  const counts = new Map<string, number>();
  return {
    beginPass() {
      counts.clear();
    },
    idFor(kind, key) {
      const base = `${kind}:${slug(key) || "x"}`;
      const occurrence = counts.get(base) ?? 0;
      counts.set(base, occurrence + 1);
      const slot = `${base}#${occurrence}`;
      const existing = assigned.get(slot);
      if (existing) return existing;
      const id = occurrence === 0 ? base.replace(":", "-") : `${base.replace(":", "-")}-${occurrence + 1}`;
      assigned.set(slot, id);
      return id;
    },
    reset() {
      assigned.clear();
      counts.clear();
    },
    size() {
      return assigned.size;
    },
  };
}

/** Chave lógica de uma visita: identidade do local, nunca o dia nem a hora. */
export function stopLogicalKey(stop: { placeId?: string | undefined; name: string }): string {
  return stop.placeId ? `pid-${stop.placeId}` : stop.name;
}

/** Chave lógica de um bloco de refeição — `mealSlotId` da rota. */
export function mealLogicalKey(meal: string): string {
  return meal;
}

/** Chave lógica de blocos logísticos (check-in, check-out, aeroporto…). */
export function logisticsLogicalKey(kind: string, direction?: string): string {
  return direction ? `${kind}-${direction}` : kind;
}
